import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useGridQueryParams } from '../hooks/useGridQueryParams'
import { useVehicleSelection } from '../hooks/useVehicleSelection'
import {
  api,
  ApiError,
  Severity,
  Source,
  type ComplianceCell,
  type GridQuery,
  type VehicleGridRow,
} from '../api/client'
import type { Severity as SeverityValue } from '../api/client'
import { invalidateFleet } from '../api/invalidate'
import { ComplianceChip } from '../components/ComplianceChip'
import { AdvancedFilter } from '../components/AdvancedFilter'
import { ComplianceSummary } from '../components/ComplianceSummary'
import { SortHeader } from '../components/SortHeader'
import { SearchSelect } from '../components/SearchSelect'
import {
  documentFilterOptions,
  siteOptions,
  vehicleTypeOptions,
} from '../components/referenceOptions'
import { DocumentMark } from '../components/DocumentMark'
import { CopyButton } from '../components/CopyButton'
import { BulkRegistrySync } from '../components/BulkRegistrySync'
import '../components/AdvancedFilter.css'
import './FleetRegister.css'

/**
 * Quick filters, as EXACT worst-state bands.
 *
 * These were "at least this severe", which on real fleet data returned almost the
 * whole register for every option — 608 of 609 vehicles are already expired, so
 * "at least due soon" and "at least unknown" both matched nearly everything and
 * the filter looked broken. Exact bands partition the fleet instead.
 */
const SEVERITY_FILTERS = [
  { label: 'All', value: undefined },
  { label: 'Overdue', value: Severity.Expired },
  { label: 'Due soon', value: Severity.DueSoon },
  { label: 'Unknown', value: Severity.Unknown },
  { label: 'All valid', value: Severity.Valid },
] as const

const STRIPE_CLASS: Record<number, string> = {
  [Severity.Expired]: 'stripe--crit',
  [Severity.Unknown]: 'stripe--unk',
  [Severity.DueSoon]: 'stripe--warn',
  [Severity.Valid]: 'stripe--ok',
  [Severity.NoExpiry]: 'stripe--none',
  [Severity.NotApplicable]: 'stripe--none',
}

/**
 * A comma turns the search box into a lookup of specific registrations.
 *
 * Returns null when there is no comma, which means "treat this as a search term".
 * A single value with a trailing comma still counts as a list — somebody pasting
 * one registration from a spreadsheet cell means the lookup, not a prefix search.
 */
/**
 * The server caps a page at 200, so a longer paste cannot be diffed exactly.
 * Reporting a partial diff as complete would be worse than saying so.
 */
const MAX_REGISTRATION_LOOKUP = 200

function splitRegistrations(text: string): string[] | null {
  if (!text.includes(',')) return null

  const parts = text
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)

  return parts.length > 0 ? parts : null
}

/** What the box should read for a given URL state, whichever filter is active. */
function currentBoxText(query: GridQuery): string {
  if (query.registrations?.length) return query.registrations.join(', ')
  return query.search ?? ''
}

export function FleetRegister() {
  const navigate = useNavigate()

  // Filters live in the URL, so Back from a vehicle restores this exact view and
  // a filtered register can be sent to somebody as a link.
  const [query, setQuery] = useGridQueryParams()
  const [searchText, setSearchText] = useState(() => currentBoxText(query))

  /*
    One row open at a time. The same choice the vehicle page makes for its
    obligation documents, and for the same reason: several open panels turn a dense
    table into a page you have to scroll to compare two rows that were adjacent.
  */
  const [showFilters, setShowFilters] = useState(false)
  const queryClient = useQueryClient()

  // A working set, not a view — see the hook. It survives paging and filtering,
  // which is what makes "the twelve I found across four filters" possible.
  const { selected, toggle, toggleMany, clear } = useVehicleSelection()

  const location = useLocation()


  const onSelect = (registration: string) => navigate(`/vehicles/${registration}`)

  const registry = useQuery({
    queryKey: ['registry-status'],
    queryFn: () => api.getRegistryStatus(),
    staleTime: 10 * 60 * 1000,
  })

  const reference = useQuery({
    queryKey: ['reference'],
    queryFn: () => api.getReference(),
    staleTime: 30 * 60 * 1000,
  })

  /**
   * Clicking the same segment twice clears it. Without that, a click that lands
   * on the wrong band leaves the user hunting for how to undo it.
   */
  function applyObligationFilter(obligationTypeId: number, severity: SeverityValue) {
    setQuery((q) =>
      q.obligationTypeId === obligationTypeId && q.obligationSeverity === severity
        ? { ...q, obligationTypeId: undefined, obligationSeverity: undefined, page: 1 }
        : { ...q, obligationTypeId, obligationSeverity: severity, page: 1 },
    )
  }

  const sync = useMutation({
    mutationFn: (vehicleId: number) => api.syncFromRegistry(vehicleId),
    onSuccess: () => invalidateFleet(queryClient),
  })

  // Everything except paging and the always-visible search box.
  const activeFilters = useMemo(() => {
    const chips: { label: string; clear: () => void }[] = []

    if (query.worstSeverity !== undefined)
      chips.push({
        label: `State: ${SEVERITY_FILTERS.find((f) => f.value === query.worstSeverity)?.label ?? '—'}`,
        clear: () => setQuery((q) => ({ ...q, worstSeverity: undefined, page: 1 })),
      })

    if (query.hasSeverity !== undefined)
      chips.push({
        label: 'Has obligation in state',
        clear: () => setQuery((q) => ({ ...q, hasSeverity: undefined, page: 1 })),
      })

    if (query.vehicleTypeId !== undefined)
      chips.push({
        label: 'Type filtered',
        clear: () => setQuery((q) => ({ ...q, vehicleTypeId: undefined, page: 1 })),
      })

    if (query.siteIds?.length)
      chips.push({
        label: `${query.siteIds.length} site${query.siteIds.length > 1 ? 's' : ''}`,
        clear: () => setQuery((q) => ({ ...q, siteIds: undefined, page: 1 })),
      })

    // The registry filters. Each names its value rather than saying "filtered", so a
    // view somebody returns to a week later still says what it is showing.
    if (query.rcStatus !== undefined)
      chips.push({
        label: `RC: ${query.rcStatus}`,
        clear: () => setQuery((q) => ({ ...q, rcStatus: undefined, page: 1 })),
      })

    if (query.fuelType !== undefined)
      chips.push({
        label: `Fuel: ${query.fuelType}`,
        clear: () => setQuery((q) => ({ ...q, fuelType: undefined, page: 1 })),
      })

    if (query.emissionNorms !== undefined)
      chips.push({
        label: `Norms: ${query.emissionNorms}`,
        clear: () => setQuery((q) => ({ ...q, emissionNorms: undefined, page: 1 })),
      })

    if (query.isFinanced !== undefined)
      chips.push({
        label: query.isFinanced ? 'Financed' : 'Not financed',
        clear: () => setQuery((q) => ({ ...q, isFinanced: undefined, page: 1 })),
      })

    if (query.financer !== undefined)
      chips.push({
        label: `Financer: ${query.financer}`,
        clear: () => setQuery((q) => ({ ...q, financer: undefined, page: 1 })),
      })

    if (query.registrations?.length)
      chips.push({
        label: `${query.registrations.length} registration${query.registrations.length > 1 ? 's' : ''}`,
        clear: () => setQuery((q) => ({ ...q, registrations: undefined, page: 1 })),
      })

    if (query.source !== undefined)
      chips.push({
        label: query.source === Source.RegistryApi ? 'Has RTO data' : 'Never synced',
        clear: () => setQuery((q) => ({ ...q, source: undefined, page: 1 })),
      })

    if (query.onlyOverridden)
      chips.push({
        label: 'Hand-corrected only',
        clear: () => setQuery((q) => ({ ...q, onlyOverridden: undefined, page: 1 })),
      })

    if (query.documentFilter !== undefined) {
      const scope = query.documentTypeId
        ? (reference.data?.complianceTypes?.find(
            (c) => c.complianceTypeId === query.documentTypeId,
          )?.displayName ?? 'obligation')
        : null

      chips.push({
        label:
          query.documentFilter === 1
            ? `Has ${scope ? `${scope.toLowerCase()} ` : ''}certificate`
            : `No ${scope ? `${scope.toLowerCase()} ` : ''}certificate`,
        clear: () =>
          setQuery((q) => ({
            ...q,
            documentFilter: undefined,
            documentTypeId: undefined,
            page: 1,
          })),
      })
    }

    // Named explicitly, because a summary-strip click is the one filter the user
    // applies without visiting a filter control — it has to be obvious what is
    // narrowing the list, and how to undo it.
    if (query.obligationTypeId !== undefined && query.obligationSeverity !== undefined) {
      const name =
        reference.data?.complianceTypes?.find(
          (c) => c.complianceTypeId === query.obligationTypeId,
        )?.displayName ?? 'Obligation'

      const band =
        {
          [Severity.Expired]: 'expired',
          [Severity.Unknown]: 'unknown',
          [Severity.DueSoon]: 'due soon',
          [Severity.Valid]: 'valid',
          [Severity.NoExpiry]: 'never expires',
          [Severity.NotApplicable]: 'not applicable',
        }[query.obligationSeverity] ?? '—'

      chips.push({
        label: `${name}: ${band}`,
        clear: () =>
          setQuery((q) => ({
            ...q,
            obligationTypeId: undefined,
            obligationSeverity: undefined,
            page: 1,
          })),
      })
    }

    /*
      The expiry window, from a click on the dashboard's runway.

      Named with its obligation when one is set — "Fitness due Oct 2026" —
      because arriving from the dashboard the user has no other clue why the
      list is short. This is the only filter that can be active WITHOUT a
      severity, so it needs its own chip rather than riding on the one above.
    */
    if (query.expiryFrom || query.expiryTo) {
      const what =
        query.obligationTypeId !== undefined
          ? (reference.data?.complianceTypes?.find(
              (c) => c.complianceTypeId === query.obligationTypeId,
            )?.displayName ?? 'Obligation')
          : 'Anything'

      chips.push({
        label: `${what} due ${describeWindow(query.expiryFrom, query.expiryTo)}`,
        clear: () =>
          setQuery((q) => ({ ...q, expiryFrom: undefined, expiryTo: undefined, page: 1 })),
      })
    }

    return chips
  }, [query, reference.data, setQuery])

  // Debounced: firing a request per keystroke would put ~10 queries in flight
  // for "MH11CH", and the last one to land — not the last one sent — would win.
  useEffect(() => {
    // Bail when the box already agrees with the URL. Without this the effect
    // fires once on mount and resets page to 1, so opening a link to page 3
    // would silently land the user on page 1.
    if (currentBoxText(query) === searchText) return

    const timer = setTimeout(() => {
      /*
        One box, two questions, told apart by a comma.

        A pasted list is a lookup of specific vehicles; a single term is a search.
        They want opposite matching rules — exact against prefix — so routing on
        content is what lets both live in the box people already use, rather than
        adding a second input nobody would find.
      */
      const list = splitRegistrations(searchText)

      setQuery((q) =>
        list
          ? { ...q, registrations: list, search: undefined, page: 1 }
          : { ...q, search: searchText || undefined, registrations: undefined, page: 1 },
      )
    }, 250)

    return () => clearTimeout(timer)
  }, [searchText, query, setQuery])

  /*
    A pasted list is fetched as one page that holds all of it.

    The "Not in the register" diff below compares the list with the rows on
    screen, so it is only exact when every match is on screen. At the default
    40 rows, a 67-registration list put 27 real vehicles on page 2 and named
    every one of them as missing — reported against the KA/DD container list,
    and believable, because they had only just been imported.
  */
  const fetchQuery = useMemo<GridQuery>(
    () =>
      query.registrations?.length
        ? {
            ...query,
            page: 1,
            pageSize: Math.min(
              Math.max(query.registrations.length, query.pageSize ?? 0),
              MAX_REGISTRATION_LOOKUP,
            ),
          }
        : query,
    [query],
  )

  const { data, error, isFetching, isPending } = useQuery({
    queryKey: ['vehicles', fetchQuery],
    queryFn: () => api.getVehicles(fetchQuery),
    // Keeps the previous page visible while the next loads, so paging does not
    // blank the table and shift the layout under the user's finger.
    placeholderData: keepPreviousData,
  })

  // Memoised on `data`, not written inline: `data?.items ?? []` is a fresh array
  // identity on every render, so the summary memo below it never hit and oxlint
  // warned about exactly that.
  const rows = useMemo(() => data?.items ?? [], [data])
  const pageIds = rows.map((r) => r.vehicleId!).filter(Boolean)
  const total = data?.totalRows ?? 0
  const totalPages = data?.totalPages ?? 0
  const page = query.page ?? 1

  const summary = useMemo(
    () => ({
      overdue: rows.filter((r) => r.worstSeverity === Severity.Expired).length,
      unknown: rows.filter((r) => r.worstSeverity === Severity.Unknown).length,
      notSynced: rows.filter((r) => !r.hasRegistryData).length,
    }),
    [rows],
  )

  /*
    A pasted list is a lookup, not a browse: ask for a page that covers the whole
    list so the diff below is exact. Capped at the server's own page ceiling.
  */
  const requestedRegistrations = query.registrations
  const listTruncated = (requestedRegistrations?.length ?? 0) > MAX_REGISTRATION_LOOKUP

  const missingRegistrations = useMemo(() => {
    if (!requestedRegistrations?.length || isPending) return []

    // Canonicalised only for comparison, never for the request — the server owns
    // the real rule. This must match it: strip spaces, dashes, dots, slashes.
    const canon = (r: string) => r.toUpperCase().replace(/[\s\-./]/g, '')
    const found = new Set(rows.map((r) => canon(r.registration ?? '')))

    return requestedRegistrations
      .slice(0, MAX_REGISTRATION_LOOKUP)
      .filter((r) => !found.has(canon(r)))
  }, [requestedRegistrations, rows, isPending])

  const offline = error instanceof ApiError && error.isOffline

  return (
    <div className="register">
      <header className="register__head">
        <div>
          {/* Matches the tab that reaches it. A tab reading "Vehicles" that
              lands on a page headed "Fleet Due Date" makes the user wonder
              whether they arrived somewhere else. The module name moves to the
              eyebrow, mirroring the dashboard's header. */}
          <p className="eyebrow">Fleet Due Date</p>
          <h1>Vehicles</h1>
          <p className="register__sub">
            Compliance register — insurance, road tax, fitness, permit, PUC
          </p>
        </div>

        <div className="register__right">
          <div className="register__stats">
            <div className="statcard">
              <b className="mono">{total.toLocaleString()}</b>
              <span>Vehicles</span>
            </div>
            <div className="statcard statcard--crit">
              <b className="mono">{summary.overdue}</b>
              <span>Overdue here</span>
            </div>
            <div className="statcard statcard--unk">
              <b className="mono">{summary.unknown}</b>
              <span>Unknown here</span>
            </div>
            <div className="statcard statcard--accent">
              <b className="mono">{summary.notSynced}</b>
              <span>Not RTO&nbsp;synced</span>
            </div>
          </div>

          <div className="register__actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => navigate('/vehicles/new')}
            >
              + Add vehicle
            </button>
            <button type="button" className="btn" onClick={() => navigate('/import')}>
              Import Excel
            </button>
            {/*
              Beside Import, and always available — two of its three scopes need
              nothing ticked. "Build report" stays in the selection bar because
              a report IS a set of chosen vehicles; an export is not.
            */}
            <button
              type="button"
              className="btn"
              onClick={() => {
                /*
                    The register's filters are already in the URL, so they are
                    carried across verbatim and re-parsed there by the same hook
                    — no second serialiser, and no way for the two to disagree
                    about what "the current filter" means.

                    Ticked rows ride as ?ids=, the way /report takes them: the
                    selection lives in a hook scoped to this page and would be
                    empty after a navigation.
                */
                const params = new URLSearchParams(location.search)
                if (selected.size > 0) params.set('ids', [...selected].join(','))
                // Paging is the register's business, not the export's.
                params.delete('page')
                params.delete('pageSize')
                navigate(`/export?${params.toString()}`)
              }}
            >
              Export Excel
            </button>
          </div>
        </div>
      </header>

      {offline && (
        <div className="banner banner--offline" role="status">
          <b>You are offline.</b> Showing the last data loaded on this device. Editing is
          unavailable until you reconnect.
        </div>
      )}

      {error && !offline && (
        <div className="banner banner--error" role="alert">
          {(error as Error).message}
        </div>
      )}

      {/* Fleet-wide, above the table. The first question is "how bad is it, and
          where" — five stacked bars answer that far faster than paging 610 rows. */}
      <ComplianceSummary query={query} onFilter={applyObligationFilter} />

      <div className="controls">
        <input
          type="search"
          className="mono"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search registration, chassis, code — or paste a list…"
          aria-label="Search vehicles"
        />

        {/* Site and type inline: these are the two filters used constantly, and
            burying them behind a modal made routine work slower.

            SearchSelect rather than a native <select>: 16 sites and 20 types are
            past the point where scrolling a popup beats typing, and the platform
            popup draws its own blue highlight that no stylesheet can reach. */}
        <div className="ctl-select">
          <SearchSelect
            options={siteOptions(reference.data)}
            value={query.siteId ?? ''}
            onChange={(siteId) =>
              setQuery((q) => ({ ...q, siteId: siteId === '' ? undefined : siteId, page: 1 }))
            }
            emptyLabel="All sites"
            placeholder="Search sites…"
          />
        </div>

        <div className="ctl-select">
          <SearchSelect
            // Machinery is excluded from the register itself, so offering it as a
            // filter would be offering a guaranteed empty result.
            options={vehicleTypeOptions(reference.data).filter((o) => o.hint !== 'machinery')}
            value={query.vehicleTypeId ?? ''}
            onChange={(vehicleTypeId) =>
              setQuery((q) => ({
                ...q,
                vehicleTypeId: vehicleTypeId === '' ? undefined : vehicleTypeId,
                page: 1,
              }))
            }
            emptyLabel="All types"
            placeholder="Search types…"
          />
        </div>

        <div className="seg" role="group" aria-label="Filter by status">
          {SEVERITY_FILTERS.map((filter) => (
            <button
              key={filter.label}
              type="button"
              aria-pressed={query.worstSeverity === filter.value}
              onClick={() => setQuery((q) => ({ ...q, worstSeverity: filter.value, page: 1 }))}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* One-tap access to the question asked most often once RTO sync is live:
            which vehicles have never been confirmed against the registry. */}
        <button
          type="button"
          className={`btn btn--rtofilter ${query.source === Source.ExcelImport ? 'btn--rtofilter-on' : ''}`}
          onClick={() =>
            setQuery((q) => ({
              ...q,
              source: q.source === Source.ExcelImport ? undefined : Source.ExcelImport,
              page: 1,
            }))
          }
        >
          Not RTO synced
        </button>

        {/*
          The question that turns a register of dates into a list of work: which
          vehicles have nothing on file. Unscoped, so it means "no certificate for
          any obligation" — a vehicle with four of five still counts as covered
          here, and the per-obligation form lives in the advanced filter.
        */}
        <div className="ctl-select ctl-select--wide">
          <SearchSelect
            // Composite string values — "2:5" is "missing PUC certificate" — which
            // is why SearchSelect is generic over its value type rather than fixed
            // to an id.
            options={documentFilterOptions(reference.data)}
            value={
              query.documentFilter
                ? `${query.documentFilter}:${query.documentTypeId ?? ''}`
                : ''
            }
            onChange={(choice) => {
              const [filter, typeId] = String(choice).split(':')
              setQuery((q) => ({
                ...q,
                documentFilter: filter ? (Number(filter) as 1 | 2) : undefined,
                documentTypeId: typeId ? Number(typeId) : undefined,
                page: 1,
              }))
            }}
            emptyLabel="All vehicles"
            placeholder="Search certificate filters…"
          />
        </div>

        <button type="button" className="btn btn--filter" onClick={() => setShowFilters(true)}>
          Advanced filter
          {/* A pill rather than "(2)": the parenthesised number sat in the button's
              own colour and was effectively invisible at this size. */}
          {activeFilters.length > 0 && (
            <span className="btn__count">{activeFilters.length}</span>
          )}
        </button>

        <span className="controls__count mono">
          {isFetching && !isPending ? 'updating…' : `${total.toLocaleString()} vehicles`}
        </span>
      </div>

      {/* Active filters stay visible, so a filtered view showing few rows never
          gets mistaken for an empty register. */}
      {activeFilters.length > 0 && (
        <div className="activefilters">
          <span className="activefilters__label">Filtered by</span>
          {activeFilters.map((chip) => (
            <span key={chip.label} className="afchip">
              {chip.label}
              <button type="button" onClick={chip.clear} aria-label={`Remove ${chip.label}`}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/*
        Which of the pasted registrations are not in the register.

        Pasting forty numbers out of an email and being shown thirty-seven, with
        nothing to say the other three were never found, is the failure worth
        preventing here — the missing ones are exactly the vehicles somebody is
        asking about.

        Diffed locally against what came back rather than reported by the server:
        the register requests a page big enough to cover the list, so the answer is
        exact and costs no extra round trip.
      */}
      {missingRegistrations.length > 0 && (
        <div className="activefilters">
          <span className="activefilters__label">Not in the register</span>
          {missingRegistrations.map((reg) => (
            <span key={reg} className="afchip afchip--missing mono">
              {reg}
            </span>
          ))}
        </div>
      )}

      {listTruncated && (
        <div className="banner banner--error">
          That is more than {MAX_REGISTRATION_LOOKUP} registrations. Only the first{' '}
          {MAX_REGISTRATION_LOOKUP} were looked up, and vehicles beyond that are not
          reported as missing either way.
        </div>
      )}

      {/*
        Appears only once something is chosen. A permanent bar advertising a
        feature that cannot yet be used is chrome; this one arrives at the moment
        it becomes usable and says how many are in the set — which is the fact the
        user is otherwise tracking in their head across four pages.
      */}
      {/*
        Offered only under "Not RTO synced", where the set it acts on is the set
        on screen. Elsewhere a "sync all" would have to explain which of the rows
        shown it meant, and the answer — the unsynced ones among them — is exactly
        what the filter already says.
      */}
      {query.source === Source.ExcelImport && registry.data?.isConfigured && total > 0 && (
        <BulkRegistrySync
          query={query}
          total={total}
          unitCostPaise={registry.data.unitCostPaise}
        />
      )}

      {selected.size > 0 && (
        <div className="selbar" role="status">
          <span className="selbar__count">
            <b>{selected.size}</b> vehicle{selected.size > 1 ? 's' : ''} chosen
          </span>

          <div className="selbar__actions">
            <button type="button" className="btn" onClick={clear}>
              Clear
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => navigate(`/report?ids=${[...selected].join(',')}`)}
            >
              Build report
            </button>
          </div>
        </div>
      )}

      {/*
        Sorting, for the card layout.

        Below the card breakpoint the column headings are gone, and with them the
        only way to reorder the register - a phone would have been stuck with the
        default order and no way out of it but editing the URL. Hidden above that
        width, where the headings do the job properly.
      */}
      <div className="msort">
        <label>
          <span>Sort</span>
          <select
            value={query.sortBy ?? ''}
            onChange={(e) =>
              setQuery((q) => ({
                ...q,
                sortBy: e.target.value || undefined,
                sortDescending: e.target.value ? q.sortDescending : undefined,
                page: 1,
              }))
            }
          >
            <option value="">Default order</option>
            <option value="severity">Worst obligation</option>
            <option value="reg">Registration</option>
            <option value="type">Type</option>
            <option value="make">Make / model</option>
            <option value="site">Site</option>
            <option value="insurance">Insurance</option>
            <option value="roadtax">Road tax</option>
            <option value="fitness">Fitness</option>
            <option value="permit">Permit</option>
            <option value="puc">PUC</option>
            <option value="documents">Certificates on file</option>
          </select>
        </label>
        <button
          type="button"
          className="msort__dir"
          disabled={!query.sortBy}
          aria-label={query.sortDescending ? 'Sort ascending' : 'Sort descending'}
          title={query.sortDescending ? 'Largest first' : 'Smallest first'}
          onClick={() =>
            setQuery((q) => ({ ...q, sortDescending: q.sortDescending ? undefined : true, page: 1 }))
          }
        >
          <span aria-hidden="true">{query.sortDescending ? '↓' : '↑'}</span>
        </button>
      </div>

      <div className="tablewrap">
        <table>
          {/*
            Explicit widths with table-layout:fixed. Left to auto-layout the
            browser sizes every column to its widest cell, which pushed the table
            past the viewport and forced a horizontal scrollbar. Percentages let
            it fill whatever width it is given and compress instead.
          */}
          <colgroup>
            <col className="col-pick" />
            <col className="col-stripe" />
            <col className="col-reg" />
            <col className="col-type" />
            <col className="col-make" />
            <col className="col-site" />
            <col className="col-dt" />
            <col className="col-dt" />
            <col className="col-dt" />
            <col className="col-dt" />
            <col className="col-dt" />
            <col className="col-src" />
          </colgroup>
          <thead>
            <tr>
              <th className="pick">
                <input
                  type="checkbox"
                  aria-label="Select every vehicle on this page"
                  checked={pageIds.length > 0 && pageIds.every((id) => selected.has(id))}
                  // Partly selected is its own state: an unchecked box on a page
                  // where six of forty are picked would read as "nothing chosen".
                  ref={(el) => {
                    if (el) {
                      el.indeterminate =
                        pageIds.some((id) => selected.has(id)) &&
                        !pageIds.every((id) => selected.has(id))
                    }
                  }}
                  onChange={() => toggleMany(pageIds)}
                />
              </th>
              {/*
                The stripe column sorts by worst severity. It has no visible
                label, so the caret alone carries it — which is why the caret is
                always rendered rather than only on the active column.
              */}
              <SortHeader
                label=""
                sortKey="severity"
                query={query}
                setQuery={setQuery}
                title="Sort by worst obligation on the row"
              />
              <SortHeader label="Registration" sortKey="reg" query={query} setQuery={setQuery} />
              <SortHeader label="Type" sortKey="type" query={query} setQuery={setQuery} />
              <SortHeader label="Make / model" sortKey="make" query={query} setQuery={setQuery} />
              <SortHeader label="Site" sortKey="site" query={query} setQuery={setQuery} />
              {/*
                Date columns sort soonest-first, with the dateless rows pushed to
                the end in both directions — see the ORDER BY in
                usp_Vehicle_GetGridPage. Otherwise an ascending sort opens with
                150 blanks instead of the most overdue vehicle in the fleet.
              */}
              <SortHeader label="Insurance" sortKey="insurance" numeric query={query} setQuery={setQuery} />
              <SortHeader label="Road tax" sortKey="roadtax" numeric query={query} setQuery={setQuery} />
              <SortHeader label="Fitness" sortKey="fitness" numeric query={query} setQuery={setQuery} />
              <SortHeader label="Permit" sortKey="permit" numeric query={query} setQuery={setQuery} />
              <SortHeader label="PUC" sortKey="puc" numeric query={query} setQuery={setQuery} />
              <SortHeader
                label="Source"
                sortKey="documents"
                query={query}
                setQuery={setQuery}
                title="Sort by how many certificates are on file"
              />
            </tr>
          </thead>
          <tbody>
            {isPending && (
              <tr>
                <td colSpan={12} className="empty">
                  Loading the register…
                </td>
              </tr>
            )}

            {!isPending && rows.length === 0 && (
              <tr>
                <td colSpan={12} className="empty">
                  No vehicles match these filters.
                  <br />
                  Try clearing the search or the status filter.
                </td>
              </tr>
            )}

            {rows.map((row) => (
              <VehicleRow
                key={row.vehicleId}
                row={row}
                onSelect={onSelect}
                canSync={registry.data?.isConfigured ?? false}
                syncing={sync.isPending && sync.variables === row.vehicleId}
                onSync={() => sync.mutate(row.vehicleId!)}
                picked={selected.has(row.vehicleId!)}
                onPick={() => toggle(row.vehicleId!)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <nav className="pager" aria-label="Pagination">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setQuery((q) => ({ ...q, page: (q.page ?? 1) - 1 }))}
          >
            ← Previous
          </button>
          <span className="mono">
            page {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setQuery((q) => ({ ...q, page: (q.page ?? 1) + 1 }))}
          >
            Next →
          </button>
        </nav>
      )}

      {showFilters && (
        <AdvancedFilter
          current={query}
          onApply={setQuery}
          onClose={() => setShowFilters(false)}
        />
      )}
    </div>
  )
}

/**
 * One obligation's date, with the certificate behind it if there is one.
 *
 * The marker sits beside the chip rather than replacing any part of it: the date
 * and its severity are what the register is for, and the evidence is a footnote
 * to them.
 */
/**
 * A date window in words.
 *
 * Collapses a whole calendar month to "Oct 2026" rather than printing
 * "01-10-2026 to 31-10-2026", which is the common case arriving from the
 * dashboard and reads as noise at that length.
 */
function describeWindow(from?: string, to?: string): string {
  const day = (iso: string) => {
    const [y, m, d] = iso.slice(0, 10).split('-')
    return `${d}-${m}-${y}`
  }

  if (from && to) {
    const f = new Date(`${from.slice(0, 10)}T00:00:00`)
    const t = new Date(`${to.slice(0, 10)}T00:00:00`)
    const lastOfMonth = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate()

    const wholeMonth =
      f.getDate() === 1 &&
      f.getMonth() === t.getMonth() &&
      f.getFullYear() === t.getFullYear() &&
      t.getDate() === lastOfMonth

    return wholeMonth
      ? f.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
      : `${day(from)} to ${day(to)}`
  }

  if (from) return `on or after ${day(from)}`
  return `on or before ${day(to!)}`
}

function DateCell({
  row,
  cell,
  obligation,
}: {
  row: VehicleGridRow
  cell: ComplianceCell | undefined
  obligation: string
}) {
  if (!cell) return <td className="dt" data-label={obligation} />

  return (
    <td className="dt" data-label={obligation}>
      <span className="dtcell">
        <ComplianceChip cell={cell} />
        <DocumentMark
          vehicleId={row.vehicleId!}
          registration={row.registration!}
          obligation={obligation}
          cell={cell}
        />
      </span>
    </td>
  )
}

function VehicleRow({
  row,
  onSelect,
  canSync,
  syncing,
  onSync,
  picked,
  onPick,
}: {
  row: VehicleGridRow
  onSelect: (registration: string) => void
  canSync: boolean
  syncing: boolean
  onSync: () => void
  picked: boolean
  onPick: () => void
}) {
  const stripe = STRIPE_CLASS[row.worstSeverity ?? Severity.Unknown] ?? 'stripe--none'

  return (
    <tr
      className={picked ? 'picked' : undefined}
      tabIndex={0}
      onClick={() => onSelect(row.registration!)}
      onKeyDown={(e) => {
        // Rows are interactive, so they must be reachable and operable from the
        // keyboard, not only by pointer.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect(row.registration!)
        }
      }}
    >
      {/* Its own cell with stopPropagation: the row navigates to the vehicle, and
          ticking a box for a report must not open it. */}
      <td className="pick" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={picked}
          onChange={onPick}
          aria-label={`Include ${row.registration} in a report`}
        />
      </td>

      <td className="stripe">
        <i className={stripe} />
      </td>
      <td className="reg mono" data-label="Registration">
        {row.registration}
        {/* Copies the canonical spelling, which is what VAHAN and insurer portals
            expect — "MH 12 EQ 3823" fails those lookups. */}
        <CopyButton value={row.registration!} label="registration" />
        {row.hasManualOverride && (
          <span className="badge" title="Contains a hand-corrected value">
            edited
          </span>
        )}
      </td>
      {/* title on each: these columns truncate to fit, so the full value has to
          stay reachable without opening the row. */}
      <td className="tsm" title={row.vehicleType ?? undefined} data-label="Type">
        {row.vehicleType ?? '—'}
      </td>
      <td
        className="tsm"
        title={[row.make, row.model].filter(Boolean).join(' ') || undefined}
        data-label="Make / model"
      >
        {[row.make, row.model].filter(Boolean).join(' ') || '—'}
      </td>
      <td className="tsm" title={row.site ?? undefined} data-label="Site">
        {row.site ?? <span className="muted">unassigned</span>}
      </td>
      <DateCell row={row} cell={row.insurance} obligation="Insurance" />
      <DateCell row={row} cell={row.roadTax} obligation="Road tax" />
      <DateCell row={row} cell={row.fitness} obligation="Fitness" />
      {/*
        Permit carries no marker, and that is deliberate rather than an omission.
        Permit (4) takes no certificate; the obligation that does is All India
        Permit (6), which has no column here. Borrowing its scan for this cell
        would file one obligation's evidence under another's name.
      */}
      <td className="dt" data-label="Permit">
        {row.permit && <ComplianceChip cell={row.permit} />}
      </td>
      <DateCell row={row} cell={row.puc} obligation="PUC" />

      {/*
        Source badges. Short codes rather than words — a vehicle can carry all
        three at once (insurance from the registry, PUC typed by hand, the rest
        from the sheet), and spelling that out would not fit a table cell.

        The Sync button only appears where RTO data is absent: offering it on a
        row that is already confirmed is noise, and this is a paid per-call API.
      */}
      <td className="source" data-label="RTO record" onClick={(e) => e.stopPropagation()}>
        <span className="srctags">
          {/*
            Symbol AND word. This was a bare dot for a while, because the earlier
            "NO RTO" tag wrapped inside an 8%-wide fixed-layout column and made
            every unsynced row taller. The dot fixed the height and lost the
            meaning: a mark whose legend is only in a tooltip is one nobody
            reads on a printout or at a glance.

            So the label is back, and the width is bought honestly instead —
            col-src 8% -> 11%, taken 0.6% at a time off the five date columns.
            nowrap on the pill is what stops it wrapping again; without it this
            regresses to the version that was rejected.
          */}
          {/*
            Where a record exists the marker IS the way into it — one control in
            the width of one, which is what this column can afford. It replaced an
            expanding row: a second table unfolding inside the first was crowded
            and hard to read, and everything it held is on the RTO page anyway.

            Where no record exists there is nothing to open, so the marker stays a
            plain mark and the Sync button beside it is the only thing to press.
          */}
          {row.hasRegistryData ? (
            <Link
              to={`/vehicles/${row.registration}/rto`}
              className="rtopill rtopill--yes rtopill--go"
              title={
                row.registryVerifiedUtc
                  ? `See the full RTO record — confirmed on ${new Date(row.registryVerifiedUtc).toLocaleDateString()}`
                  : 'See the full RTO record'
              }
              aria-label={`See the full RTO record for ${row.registration}`}
            >
              <span className="rtopill__ico" aria-hidden="true">
                ✓
              </span>
              <span className="rtopill__txt">RTO record</span>
              <span className="rtopill__go" aria-hidden="true">
                ›
              </span>
            </Link>
          ) : (
            <span className="rtopill rtopill--no" title="Never confirmed against the RTO">
              <span className="rtopill__ico" aria-hidden="true">
                –
              </span>
              <span className="rtopill__txt">RTO</span>
            </span>
          )}
          {row.hasManualData && (
            <span className="srctag srctag--man" title="Contains values typed by a person">
              MAN
            </span>
          )}
          {row.hasSheetData && (
            <span className="srctag srctag--xls" title="Contains values from a spreadsheet import">
              XLS
            </span>
          )}

          {/*
            The same pill shape as the marker beside it, so the cell reads as one
            rank rather than a badge with a control bolted on. Inside the flex
            row, never under it — as a block it sat on its own line and added back
            exactly the row height the dot was introduced to remove.
          */}
          {!row.hasRegistryData && (
            <button
              type="button"
              className="rtopill rtopill--btn"
              disabled={!canSync || syncing}
              title={
                canSync
                  ? 'Fetch current details from VAHAN'
                  : 'Requires a licensed VAHAN aggregator subscription'
              }
              aria-label="Sync this vehicle from the RTO"
              onClick={onSync}
            >
              <span className="rtopill__ico" aria-hidden="true">
                {syncing ? '⋯' : '⟳'}
              </span>
              <span className="rtopill__txt">{syncing ? 'Syncing' : 'Sync'}</span>
            </button>
          )}
        </span>
      </td>
    </tr>
  )
}
